import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Lock, Users, ArrowRight, MonitorPlay } from "lucide-react";

// Inline Discord glyph so we don't add another dependency.
const DiscordIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20.317 4.369A19.79 19.79 0 0 0 16.885 3.3a.075.075 0 0 0-.079.037c-.34.6-.719 1.383-.984 2.005a18.27 18.27 0 0 0-5.487 0A12.5 12.5 0 0 0 9.34 3.337.077.077 0 0 0 9.261 3.3 19.72 19.72 0 0 0 5.83 4.369a.07.07 0 0 0-.032.027C2.548 9.043 1.635 13.579 2.079 18.058a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.994 3.03.078.078 0 0 0 .085-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.104 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .078-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .079.01c.12.099.246.197.373.291a.077.077 0 0 1-.006.128 12.3 12.3 0 0 1-1.873.891.076.076 0 0 0-.041.105c.36.699.772 1.363 1.225 1.993a.076.076 0 0 0 .084.029 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .031-.055c.5-5.177-.838-9.674-3.549-13.66a.06.06 0 0 0-.031-.028zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.955 2.418-2.157 2.418zm7.974 0c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.211 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [discordCfg, setDiscordCfg] = useState({ enabled: false });
  const [bridgeCfg, setBridgeCfg] = useState({ login_url: "" });
  const [showEmployee, setShowEmployee] = useState(false);
  const [discordError, setDiscordError] = useState("");
  const [ssoError, setSsoError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/config/discord");
        setDiscordCfg(data);
      } catch {}
      try {
        const { data } = await api.get(
          `/bridge/health?origin=${encodeURIComponent(window.location.origin)}`
        );
        setBridgeCfg(data);
      } catch {}
    })();
    const params = new URLSearchParams(window.location.search);
    if (params.get("discord_error")) setDiscordError(params.get("discord_error"));
    if (params.get("sso_error")) setSsoError(params.get("sso_error"));
  }, []);

  if (user) {
    const dest =
      user.role === "admin" || (user.roles && user.roles.includes("employee")) ? "/dashboard" :
      (user.roles && user.roles.includes("host")) ? "/host-studio" :
      "/watchlist";
    return <Navigate to={dest} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const me = await login(email, password);
      const dest =
        me?.role === "admin" ? "/dashboard" :
        (me?.roles && me.roles.includes("employee")) ? "/dashboard" :
        (me?.roles && me.roles.includes("host")) ? "/host-studio" :
        "/watchlist";
      nav(dest);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const startDiscord = async () => {
    try {
      const { data } = await api.get("/auth/discord/login", { params: { next: "/watchlist" } });
      window.location.href = data.url;
    } catch (e) {
      setErr("Discord login unavailable right now.");
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 relative grain" data-testid="login-page">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-black via-black/90 to-black/70" />

      <div className="relative z-10 w-full max-w-md surface-card p-10">
        <div className="flex items-center gap-3 mb-2">
          <Users className="text-[var(--ff-red)]" size={18} />
          <div className="label-eyebrow">JOIN THE NETWORK</div>
        </div>
        <h1 className="font-display text-4xl mb-3">WELCOME.</h1>
        <p className="text-[var(--ff-text-dim)] text-sm mb-8">
          Sign in with Discord to save shows and get reminders. Employees & hosts also sign in with Discord — access unlocks automatically based on your role in our server.
        </p>

        {discordError && (
          <div className="mb-4 text-[var(--ff-red)] text-xs font-mono-ui uppercase tracking-[0.15em]" data-testid="discord-error">
            {discordError === "discord_disabled"
              ? "Discord sign-in is temporarily disabled by an admin."
              : `Discord sign-in failed: ${discordError}`}
          </div>
        )}

        {ssoError && (
          <div className="mb-4 text-[var(--ff-red)] text-xs font-mono-ui uppercase tracking-[0.15em]" data-testid="sso-error">
            {ssoError === "bridge_disabled"
              ? "The Community Lounge bridge is temporarily offline (Code Red)."
              : `Community Lounge sign-in failed: ${ssoError}`}
          </div>
        )}

        <button
          onClick={startDiscord}
          disabled={!discordCfg.enabled}
          className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-[#5865F2] text-white font-mono-ui text-sm uppercase tracking-[0.18em] hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="discord-login-btn"
        >
          <DiscordIcon size={20} />
          {discordCfg.enabled
            ? "Continue with Discord"
            : discordCfg.login_disabled_by_admin
            ? "Discord Login Paused"
            : "Discord Unavailable"}
          <ArrowRight size={16} />
        </button>

        {discordCfg.login_disabled_by_admin && (
          <p className="mt-3 text-[0.65rem] font-mono-ui text-[var(--ff-red)] uppercase tracking-[0.15em]" data-testid="discord-login-disabled-notice">
            Discord login temporarily unavailable. Please check back soon.
          </p>
        )}

        {bridgeCfg.login_url && (
          <button
            onClick={() => { window.location.href = bridgeCfg.login_url; }}
            className="w-full mt-3 flex items-center justify-center gap-3 px-4 py-4 border border-[var(--ff-border)] text-white font-mono-ui text-sm uppercase tracking-[0.18em] hover:bg-white/5 transition-all"
            data-testid="community-lounge-login-btn"
          >
            <MonitorPlay size={20} />
            Continue with Community Lounge
            <ArrowRight size={16} />
          </button>
        )}

        <p className="mt-3 text-[0.65rem] font-mono-ui text-[var(--ff-text-dim)] uppercase tracking-[0.15em]">
          Fans · Free · No password needed
        </p>

        <div className="mt-10 pt-6 border-t border-[var(--ff-border)]">
          {!showEmployee ? (
            <button
              onClick={() => setShowEmployee(true)}
              className="w-full flex items-center justify-center gap-2 label-eyebrow text-[var(--ff-text-dim)] hover:text-white transition-colors"
              data-testid="show-employee-portal-btn"
            >
              <Lock size={12} /> Admin Login →
            </button>
          ) : (
            <div data-testid="employee-portal-section">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="text-[var(--ff-red)]" size={16} />
                <div className="label-eyebrow">ADMIN LOGIN</div>
              </div>
              <p className="text-[var(--ff-text-dim)] text-xs mb-4">
                Site administrators only. Employees and hosts should sign in with Discord above.
              </p>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="label-eyebrow block mb-1">Email</label>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2.5 text-sm" data-testid="login-email-input" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">Password</label>
                  <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-2.5 text-sm" data-testid="login-password-input" />
                </div>
                {err && <div className="text-[var(--ff-red)] text-xs font-mono-ui" data-testid="login-error">{err}</div>}
                <button disabled={loading} className="btn-primary w-full justify-center text-xs" data-testid="login-submit-btn">
                  {loading ? "Signing in..." : "Sign In →"}
                </button>
                <button type="button" onClick={() => setShowEmployee(false)} className="w-full label-eyebrow text-[var(--ff-text-dim)] hover:text-white transition-colors mt-2" data-testid="hide-employee-portal-btn">
                  ← Back
                </button>
              </form>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="label-eyebrow text-[var(--ff-text-dim)] hover:text-white transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
